import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import { queryClient } from "App";
import { addResizedColumns } from "hooks/useTableConfig";
import { ACCOUNTS_TABLE_CONSTANTS } from "routes/constant_contact/components/accounts_table/AccountsTableV3";
import { getAccountsTableColumns } from "routes/constant_contact/components/accounts_table/AccountsTableColumnsAndFilters";
import ConstantContactAccountsService from "services/ConstantContactAccounts";
import UserService from "services/User";

export const invalidateAccountsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [ACCOUNTS_TABLE_CONSTANTS.queryKey],
  });
};

export const useAccountMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => ConstantContactAccountsService.create(data),
    onSuccess: () => {
      invalidateAccountsQuery();
      toast.success("Account created");
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }) =>
      ConstantContactAccountsService.update(id, data),
    onSuccess: () => {
      invalidateAccountsQuery();
      toast.success("Account updated");
    },
  });

  const remove = useMutation({
    mutationFn: id => ConstantContactAccountsService.delete(id),
    onSuccess: () => {
      invalidateAccountsQuery();
      toast.success("Account deleted");
      onDeleted?.();
    },
    onError: () => toast.error("Failed to delete account"),
  });

  return { create, update, remove };
};

export const useAccountsTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getAccountsTableColumns();
    const accountsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${ACCOUNTS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: accountsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched]);

  return { columns };
};
